import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import {
  POSTHOG_FEATURE_FLAGS,
  useFeatureFlagsReady,
  usePostHogFeatureFlag
} from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { FormSection, WizardHeader } from '@percent/domain/giving'
import { FullPageFormLayout, Loader, Typography } from '@percent/lemonade'
import { useQuery } from '@percent/utility'
import type { Alpha3Code } from 'i18n-iso-countries'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import commonStyles from '../opportunity-common.module.scss'
import { CancellationModal } from '../components/CancellationModal'
import { SuccessModal } from '../components/SuccessModal'
import { UnexpectedErrorModal } from '../components/UnexpectedErrorModal'
import { PartnerField } from '../components/PartnerField'
import { NameField } from '../components/NameField'
import { DescriptionField } from '../components/DescriptionField'
import { CoverImageSection } from '../components/CoverImageSection'
import { DateFields } from '../components/DateFields'
import { LocationSection } from '../components/LocationSection'
import { AttendeesSection } from '../components/AttendeesSection'
import { SkillsAndActivitiesSection } from '../components/SkillsAndActivitiesSection'
import { useEditOpportunityForm } from './useEditOpportunityForm'

export function EditOpportunityForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: opportunityId } = useParams<{ id: string }>()
  const volunteeringSection = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_VOLUNTEERING_SECTION)
  const isReady = useFeatureFlagsReady()
  const { volunteeringService } = useServices()

  const { data: volunteeringPartners, isPending: isLoadingPartners } = useQuery(
    ['volunteering-partners'],
    volunteeringService.getVolunteeringPartners,
    {},
    { enabled: volunteeringSection }
  )

  const { data: opportunityData, isPending: isLoadingOpportunity } = useQuery(
    ['getOpportunityDetails', opportunityId as string],
    volunteeringService.getVolunteeringOpportunityDetails,
    opportunityId as string,
    { enabled: !!opportunityId }
  )

  const hasVolunteeringPartners = volunteeringPartners && volunteeringPartners.length > 0

  useEffect(() => {
    if (!isReady) {
      return
    }

    if (
      !volunteeringSection ||
      !hasVolunteeringPartners ||
      (opportunityData && !volunteeringPartners.some(partner => partner.id === opportunityData.partnerId))
    ) {
      navigate(RoutePath.HOME)
    }
  }, [isReady, volunteeringSection, hasVolunteeringPartners, opportunityData, volunteeringPartners, navigate])

  const formProps = useEditOpportunityForm({
    opportunityData: opportunityData ?? undefined
  })

  if (isLoadingOpportunity || formProps.partnerConfig.isLoading) {
    return <Loader />
  }

  return (
    <FullPageFormLayout
      actionsHeader={
        <WizardHeader
          className={commonStyles.WizardHeader}
          title={t('volunteering.opportunity.form.edit.wizardHeader.title')}
          onCancel={formProps.handleCancelForm}
          cancelBtnLabel={t('button.cancel')}
          onPrimaryBtn={formProps.handleSubmit}
          primaryBtnLabel={t('button.publish')}
          isPrimaryBtnDisabled={!formProps.formValid}
          primaryBtnDisabledTooltipCopy={t(
            'volunteering.opportunity.form.create.wizardHeader.publishBtnDisabledTooltip'
          )}
          isPrimaryBtnLoading={formProps.isSubmitting}
        />
      }
    >
      <Typography variant="h2" style={{ marginBottom: '1.5rem' }}>
        {t('volunteering.opportunity.form.create.subtitle')}
      </Typography>

      <form className={commonStyles.Form} onSubmit={event => event.preventDefault()}>
        <FormSection title={t('volunteering.opportunity.form.common.overviewSection.title')}>
          <PartnerField
            availablePartners={volunteeringPartners ?? []}
            isLoading={isLoadingPartners}
            value={opportunityData?.partnerId}
            onChange={() => {
              /* no-op */
            }}
            readOnly={true}
          />

          <NameField
            value={formProps.values.name}
            touched={formProps.touched.name}
            error={formProps.errors.name}
            onChange={formProps.handleChange}
            onBlur={formProps.handleBlur}
          />

          <DescriptionField
            value={formProps.values.description}
            onChange={formProps.setDescription}
            touched={!!formProps.touched.description}
            setTouched={formProps.setDescriptionTouched}
            error={formProps.errors.description}
            onBlur={formProps.handleBlur}
          />
        </FormSection>

        <CoverImageSection
          value={formProps.coverImage}
          rawValue={formProps.values.image}
          onChange={formProps.handleCoverImageChange}
          error={formProps.coverImageError}
          opportunityName={formProps.values.name}
          handleCoverImageChange={formProps.handleCoverImageChange}
        />

        <FormSection title={t('volunteering.opportunity.form.common.detailsSection.title')}>
          <DateFields
            dateValue={[formProps.values.date?.[0] ?? null, formProps.values.date?.[1] ?? null]}
            startTimeValue={formProps.values.startTime}
            endTimeValue={formProps.values.endTime}
            onStartTimeChange={formProps.setStartTime}
            onEndTimeChange={formProps.setEndTime}
            dateError={formProps.errors.date}
            startTimeError={formProps.errors.startTime}
            endTimeError={formProps.errors.endTime}
            dateTouched={formProps.touched.date}
            startTimeTouched={formProps.touched.startTime}
            endTimeTouched={formProps.touched.endTime}
            setStartTimeTouched={formProps.setStartTimeTouched}
            setEndTimeTouched={formProps.setEndTimeTouched}
            setDateTouched={formProps.setDateTouched}
            onDateChange={formProps.setDateValue}
            onBlur={formProps.handleBlur}
          />
        </FormSection>

        <LocationSection
          value={formProps.values}
          touched={formProps.touched}
          errors={formProps.errors}
          onChange={(k, v) => formProps.setFieldValue(k, v as Alpha3Code[] | string | number)}
          handleFormChange={formProps.handleChange}
          setTouched={formProps.setFieldTouched}
          onBlur={formProps.handleBlur}
          partnerConfig={formProps.partnerConfig}
          validate={formProps.validateFields}
          preFillAddressLookup
        />

        <AttendeesSection
          participantSpots={formProps.values.participantSpots}
          onParticipantSpotsChange={formProps.setParticipantSpots}
          onBlur={formProps.handleBlur}
          participantSpotsTouched={formProps.touched.participantSpots}
          participantSpotsError={formProps.errors.participantSpots}
          notificationsEnabled={formProps.values.notificationsEnabled}
          onNotificationsEnabledChange={formProps.setNotificationsEnabled}
        />

        <SkillsAndActivitiesSection
          values={formProps.values}
          errors={formProps.errors}
          touched={formProps.touched}
          setFieldTouched={formProps.setFieldTouched}
          partnerConfig={formProps.partnerConfig}
          onSkillsChange={formProps.setSkills}
          onActivitiesChange={formProps.setActivities}
        />
      </form>

      <CancellationModal
        {...formProps.cancellationModalProps}
        description={t('volunteering.opportunity.form.edit.confirmCancellationModal.description')}
      />
      <SuccessModal {...formProps.successModalProps} />
      <UnexpectedErrorModal {...formProps.errorModalProps} />
      <UnexpectedErrorModal {...formProps.loadPartnerConfigErrorModal} />
    </FullPageFormLayout>
  )
}
